'use client';

import type { ToggleEventHandler } from 'react';
import { useState } from 'react';

import { Button } from '#/components/Button/Button';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { Icon } from '#/components/Icon/Icon';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';

import { hamburgerMenuDataByLocale } from './data/hamburgerMenuDataByLocale';
import type {
  HamburgerMenuExpandableRowType,
  HamburgerMenuIcon,
} from './data/types/HamburgerMenuData';
import {
  HamburgerMenuHolder,
  HamburgerMenuLogoutLink,
  HamburgerMenuPanelBody,
  HamburgerMenuPanelHeader,
  HamburgerMenuPanelHolder,
  HamburgerMenuRowsHolder,
  HamburgerMenuSectionHolder,
  HamburgerMenuSectionTitle,
} from './HamburgerMenu.styled';
import { hamburgerMenuTrackingName } from './hamburgerMenuConstants';
import { HamburgerMenuRow, HamburgerMenuRowContent } from './HamburgerMenuRow';
import { useIsHamburgerMenuEnabled } from './useIsHamburgerMenuEnabled';

type Props = {
  locale: I18nLocale;
};

const emptyExpandedRowTypes: ReadonlySet<HamburgerMenuExpandableRowType> =
  new Set();

const logoutIcon: HamburgerMenuIcon = { kind: 'none' };

export function HamburgerMenu({ locale }: Props) {
  const { i18n } = useI18n();
  const { user } = useUser();
  // TODO(@paprikka): temporary toggle, drop once sc-68335 is ready to be deployed
  const isEnabled = useIsHamburgerMenuEnabled();

  const [expandedRowTypes, setExpandedRowTypes] = useState<
    ReadonlySet<HamburgerMenuExpandableRowType>
  >(emptyExpandedRowTypes);

  if (!isEnabled) return null;

  const { sections, accountSectionTitle } = hamburgerMenuDataByLocale[locale];

  const toggleExpanded = (type: HamburgerMenuExpandableRowType) => {
    setExpandedRowTypes((current) => {
      const next = new Set(current);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // The panel always opens collapsed: reset expandable rows on close
  const onToggle: ToggleEventHandler<HTMLDetailsElement> = (event) => {
    if (event.currentTarget.open) return;
    setExpandedRowTypes(emptyExpandedRowTypes);
  };

  return (
    <HamburgerMenuHolder data-qa-id="hamburger-menu">
      <Dropdown
        trigger={
          <LayoutHeaderButton
            variant="grey"
            data={{ type: 'inert' }}
            qaId="hamburger-menu-toggle"
            ariaLabel={i18n('Menu')}
            smallHorizontalPaddingOnMobile
          >
            <Icon name="hamburger" size={24} />
          </LayoutHeaderButton>
        }
        // Open only: rich-details doesn't track closing
        trackClickedOpen={{ name: hamburgerMenuTrackingName }}
        onToggle={onToggle}
      >
        <HamburgerMenuPanelHolder data-qa-id="hamburger-menu-panel">
          <HamburgerMenuPanelHeader>
            <Button
              type="button"
              aria-label={i18n('Close menu')}
              variant="tertiary-grey"
              icon={{ name: 'close' }}
              qaId="hamburger-menu-close"
              clickActions={{ closeRichDetailsFromWithin: true }}
            />
          </HamburgerMenuPanelHeader>

          <HamburgerMenuPanelBody>
            {sections.map((section) => (
              <HamburgerMenuSectionHolder
                key={section.type}
                data-qa-id={`hamburger-menu-section-${section.type}`}
              >
                <HamburgerMenuSectionTitle>
                  {section.title}
                </HamburgerMenuSectionTitle>

                <HamburgerMenuRowsHolder>
                  {section.rows.map((row) => (
                    <HamburgerMenuRow
                      key={row.type}
                      row={row}
                      trackingContext={hamburgerMenuTrackingName}
                      expandedRowTypes={expandedRowTypes}
                      onToggleExpanded={toggleExpanded}
                    />
                  ))}
                </HamburgerMenuRowsHolder>
              </HamburgerMenuSectionHolder>
            ))}

            {user && (
              <HamburgerMenuSectionHolder data-qa-id="hamburger-menu-section-account">
                <HamburgerMenuSectionTitle>
                  {accountSectionTitle}
                </HamburgerMenuSectionTitle>

                <HamburgerMenuRowsHolder>
                  <li>
                    <HamburgerMenuLogoutLink
                      qaId="hamburger-menu-logout-link"
                      type="link"
                      href="/logout"
                      anchorType="native"
                    >
                      <HamburgerMenuRowContent
                        icon={logoutIcon}
                        title={i18n('Log Out')}
                      />
                    </HamburgerMenuLogoutLink>
                  </li>
                </HamburgerMenuRowsHolder>
              </HamburgerMenuSectionHolder>
            )}
          </HamburgerMenuPanelBody>
        </HamburgerMenuPanelHolder>
      </Dropdown>
    </HamburgerMenuHolder>
  );
}
